import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, Truck, ShieldCheck, Globe2 } from "lucide-react";
import Reveal from "./Reveal";
import Tilt3D from "./Tilt3D";

export function TrustBar() {
  const items = [
    [Truck, "Complimentary worldwide delivery"],
    [ShieldCheck, "Numbered certificate of authenticity"],
    [Gift, "Gold Signature Wrapping"],
    [Globe2, "A Creation of Around The World Ltd"],
  ];
  return (
    <div data-testid="trust-bar" className="relative bg-coal text-ivory border-y border-gold/25">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold-light/70 to-transparent" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map(([Icon, t], i) => (
          <motion.div key={t} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.6 }} className="flex items-center gap-3 text-[9px] md:text-[10px] tracking-[0.25em] uppercase text-ivory/65">
            <Icon size={15} className="text-gold-light shrink-0" />
            <span>{t}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export function GiftPromo() {
  return (
    <section data-testid="gift-promo" className="relative py-20 md:py-28 px-6 lg:px-12 bg-obsidian text-ivory overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_50%,rgba(212,175,55,0.14),transparent_55%)] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <Tilt3D max={7} className="aspect-[4/3] overflow-hidden gold-glow-hover border border-gold/20">
            <img src="/brand/gift-wrap.jpg" alt="Gold Signature Wrapping" loading="lazy" className="h-full w-full object-cover" />
          </Tilt3D>
        </Reveal>
        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal>
            <p className="text-[10px] md:text-xs tracking-[0.5em] uppercase gold-text mb-5">Signature Wrapping</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[0.95]">Wrapped in gold, <span className="italic gold-text">sealed by hand</span></h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-7 text-ivory/60 leading-relaxed font-light">Gold paper, a black satin bow, the ATW wax seal and a note written by hand on ivory card. Add it to any order at checkout for £15 — the handwritten note is our gift.</p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="gift-promo-shop" className="gold-button px-8 py-4 text-[11px] tracking-[0.25em] uppercase font-medium"><span className="relative z-10">Choose a gift</span></Link>
              <Link to="/legal/authenticity" data-testid="gift-promo-authenticity" className="border border-ivory/20 px-8 py-4 text-[11px] tracking-[0.25em] uppercase text-ivory/80 hover:border-gold-light hover:text-gold-light transition-colors">Our authenticity promise</Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function Unboxing() {
  return (
    <section data-testid="unboxing-section" className="relative py-20 md:py-28 px-6 lg:px-12 bg-coal text-ivory overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_60%,rgba(212,175,55,0.14),transparent_55%)] pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-[10px] md:text-xs tracking-[0.5em] uppercase gold-text mb-5">The Unboxing</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[0.95]">Every order arrives <span className="italic gold-text">like a ceremony</span></h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-7 text-ivory/60 leading-relaxed font-light">Matte black rigid box, gold foil monogram, gold tissue, black satin ribbon and your numbered certificate of authenticity — the same presentation whether you order from London, Dubai, Karachi or Dhaka.</p>
          </Reveal>
          <Reveal delay={0.2}>
            <ul className="mt-8 space-y-3 text-sm text-ivory/70">
              {["Rigid box with gold foil ATW seal", "Numbered certificate of authenticity", "Insured, tracked delivery worldwide"].map((t) => (
                <li key={t} className="flex gap-3"><span className="text-gold-light">—</span>{t}</li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <Tilt3D max={6} className="aspect-[3/2] overflow-hidden gold-glow-hover border border-gold/20">
            <img src="/brand/packaging.jpg" alt="Around The World packaging" loading="lazy" className="h-full w-full object-cover" />
          </Tilt3D>
        </Reveal>
      </div>
    </section>
  );
}
